import PostRequest, { IPostRequest } from "csi/comm/PostRequest";
import { Action } from "csi/def/Action";
import { convertNamesStoreDataToComponentItems } from "csi/middleware/common";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import { ComponentItem } from "designSystem/types/component-item-types";
import Deferred from "dojo/Deferred";
import { NamesResult, StoreNames } from "./Names";
import QueryResults from "./QueryResults";
import { StoreId, StoreQuery, StoreQueryOptions } from "./Request";
import csiStore from "./_base";

export const enum DelayedRequestState {
	WAITING = 0,
	SEARCHING = 1,
	RESULTS = 2,
	NO_RESULTS = 3,
	RESULTS_REFRESHED = 4, // server request issued to get new results
};

const EMPTY_QUERY_RESULTS = QueryResults([]);

type DelayedRequestStateChangeFunction = (value: DelayedRequestState, oldValue?: DelayedRequestState) => void;

/**
 * DelayedRequest is used to wrap another store so as to delay invoking queries until the minimum 
 * number of characters is reached and the user pauses for the given delay. There is no such
 * functionality that I am aware of in Dojo's FilteringSelect widget, so putting the functionality
 * in the store.
 * 
 * This is useful when data store could be very large.
 */
export class DelayedRequest implements dojo.store.api.Store<NamesResult, StoreQuery, StoreQueryOptions> {
	/** Delayed Request identification, check this to distinguish from regular requests */
	readonly bDelayedRequest = true;

	/** minimum number of characters to wait before querying server */
	readonly minimumQueryLength: number;

	/** callback function called when state changes */
	onChangeState?: DelayedRequestStateChangeFunction;

	/** instruct store to skip timeout delay on next query */
	skipDelayOnNextQuery = false;

	/** starting query from source data store */
	readonly sourceQuery: Query;

	/** starting request of source data store */
	readonly sourceRequest: IPostRequest;

	/** data store to query from */
	readonly sourceStore: StoreNames;

	/** deferred object awaiting search results */
	private _lastDeferred: Deferred<dojo.store.api.QueryResults<NamesResult>>;

	/** query used in last request to server */
	private _lastQuery: StoreQuery;

	/** last results returned from server */
	private _lastResults: dojo.store.api.QueryResults<NamesResult>;

	/** interaction state of store */
	private _state = DelayedRequestState.WAITING;

	/** timeout handle from last request */
	private _timeout: number;

	get action(): Action | null {
		return this.sourceStore.action;
	}

	get actionData(): dojo.store.api.QueryResults<NamesResult> | null {
		return QueryResults(this.sourceStore.actionData ?? []);
	}

	get actionProperty(): string {
		return this.sourceStore.actionProperty;
	}

	get cssProperty(): string {
		return this.sourceStore.cssProperty;
	}

	get idProperty(): string {
		return this.sourceStore.idProperty;
	}

	/** whether this store is a delayed search */
	get isDelayedSearch(): boolean {
		return this.sourceStore.isDelayedSearch;
	}

	/** whether we are waiting for user input */
	get isWaiting(): boolean {
		return this._state === DelayedRequestState.WAITING;
	}

	get labelProperty(): string {
		return this.sourceStore.labelProperty;
	}

	get labelProperty2(): string {
		return this.sourceStore.labelProperty2;
	}

	/** store's original results */
	get originalResults(): dojo.store.api.QueryResults<NamesResult> {
		return this.sourceStore._store.data;
	}

	get queryEngine(): dojo.store.api.QueryEngine<any, any> {
		return this.sourceStore.queryEngine;
	}

	get searchProperty(): string {
		return this.sourceStore.searchProperty;
	}

	/** state of store */
	get state(): DelayedRequestState {
		return this._state;
	}

	/** call callback when state changes */
	set state(value: DelayedRequestState) {
		const { onChangeState, _state } = this;

		if (value !== _state) {
			this._state = value;
			onChangeState?.(value, _state);
		}
	}

	add<D extends dojo.store.api.PutDirectives<NamesResult>>(object: NamesResult, directives?: D): StoreId {
		return this.sourceStore.add(object, directives);
	}

	get(id: StoreId): NamesResult {
		return this.sourceStore.get(id);
	}

	getChildren(parent: NamesResult, options?: StoreQueryOptions): dojo.store.api.QueryResults<NamesResult> {
		// NA
		return null;
	}

	getIdentity(item: any): StoreId {
		return this.sourceStore.getIdentity(item);
	}

	getMetadata(object: NamesResult): Object {
		// NA
		return null;
	}

	put<D extends dojo.store.api.PutDirectives<NamesResult>>(object: NamesResult, directives?: D): StoreId {
		return this.sourceStore.put(object, directives);
	}

	query(query: StoreQuery, options?: StoreQueryOptions): dojo.store.api.QueryResults<NamesResult> {
		const { skipDelayOnNextQuery, sourceStore, _lastDeferred, _lastQuery, _lastResults, _timeout } = this;
		const oldText = this._extractQueryString(_lastQuery);
		const newText = this._extractQueryString(query);
		const isSameQuery = oldText && (newText.indexOf(oldText) === 0);
		const delay = skipDelayOnNextQuery ? 0 : this._getDelay(newText.length);

		// skip delay instruction only applies to single query
		this.skipDelayOnNextQuery = false;

		if (isSameQuery) {
			// if the new query is covered by the old query, then just query store as normal and update state
			const results = sourceStore.query(query, options);
			return this._updateStateOnResults(results, isSameQuery);
		}

		if (_timeout) {
			// clear any timeout since we will have to search again
			window.clearTimeout(_timeout);

			// resolve last query with previous results
			_lastDeferred?.resolve(_lastResults || EMPTY_QUERY_RESULTS);

			this._timeout = null;
			this._lastDeferred = null;
		}

		// if not enough characters for query, then just reply with actions
		if (newText.length === 0) {
			return EMPTY_QUERY_RESULTS;
		}

		this.state = DelayedRequestState.WAITING;

		// start timer to see if there will be new input before sending request
		this._lastDeferred = new Deferred();

		this._timeout = window.setTimeout(() => {
			// start search now that user has waited long enough
			const { _lastDeferred } = this;

			// reset members
			this._lastDeferred = null;
			this._timeout = null;

			// initiate new search
			this.state = DelayedRequestState.SEARCHING;
			this._updateStoreRequest(newText);

			Promise.all([sourceStore.query(query, options)]).then(
				(results) => {
					// update store based on search results
					this._lastQuery = query;
					this._lastResults = this._updateStateOnResults(results[0]);

					// finally resolve deferred with results
					_lastDeferred?.resolve(this._lastResults);
				},
				(err) => {
					_lastDeferred?.resolve();
				},
			);
		}, delay);

		return QueryResults(this._lastDeferred);
	}

	remove(id: StoreId): void {
		this.sourceStore.remove(id);
	}

	transaction(): dojo.store.api.Transaction {
		// NA
		return null;
	}

	/**
	 * The query object comes in looking something like this:
	 * {
	 * 		Name: <RegExp>
	 * }
	 * 
	 * The RegExp has a toString method that yields "*<text>*". 
	 * This method extracts the text from the query value.
	 */
	private _extractQueryString(query: StoreQuery): string {
		const { labelProperty, searchProperty } = this.sourceStore;
		const property = query?.[searchProperty] || query?.[labelProperty];
		const text = property?.toString();
		return text?.replace(/^\*(.*)\*$/, "$1").toLowerCase() ?? "";
	}

	/**
	 * Return delay to use for timeout based on number of characters entered so far
	 */
	private _getDelay(numCharacters: number): number {
		const { minimumQueryLength } = this;
		const seconds = Math.max(minimumQueryLength - numCharacters + 1, 1);
		return seconds * 1000;
	}

	/**
	 * Update state based on number of results and the number of results that are just actions (i.e. Go to Dialog)
	 */
	private _updateStateOnResults(results: dojo.store.api.QueryResults<NamesResult>, isSameQuery?: boolean): dojo.store.api.QueryResults<NamesResult> {
		const { actionData } = this;

		// ignore items which have property _disabled == true;
		// example - "no results" items added by common.ts -> getNamesFromAggregateStoreResponse()
		const enabledItems = results.filter((item: any) => !item._disabled);

		if (enabledItems.length > Number(actionData.total)) {
			if (!isSameQuery) {
				this.state = DelayedRequestState.RESULTS_REFRESHED;
			}
			this.state = DelayedRequestState.RESULTS;
			return results;
		} else {
			this.state = DelayedRequestState.NO_RESULTS;
			return EMPTY_QUERY_RESULTS;
		}
	}

	/** 
	 * Reset store request with query for text (as opposed to just matching text on client)
	 * NOTE: the Names store does not generate a new request based on the query object, so
	 * we are modifying the initial request to the store. This is not ideal, but only way for now
	 * to get this behavior in without major changes to Names store. A new implementation
	 * in React should avoid this.
	 */
	private _updateStoreRequest(text: string): void {
		const { sourceQuery, sourceRequest, sourceStore } = this;
		const { aggregateStoreParams, requestBinaryParameter } = sourceStore;

		if (sourceQuery) {
			// modify request if using old aggregate architecture
			const postRequest = sourceRequest as PostRequest;
			const q = new Query();
			q.importQuery(sourceQuery);

			// TODO - we might need to enhance this to search by more than just node name;
			// this won't work if Display Format is used, and may not if a custom operation is used
			q.Node(xml.PROP_NAME, xml.OP_MATCHES, xml.getServerMatchValue(text));

			postRequest.setBinaryQuery(requestBinaryParameter, q);
			sourceStore.request = sourceRequest;
			sourceStore.reset();
		} else if (aggregateStoreParams) {
			// this is an aggregate store, so use SearchText request parameter instead
			sourceStore.request = csiStore.getAggregateStoreRequest({
				searchText: text,
				...aggregateStoreParams,
			});
		}
	}

	constructor(store: StoreNames, minimumQueryLength = 3) {
		const { request } = store;
		this.minimumQueryLength = minimumQueryLength;
		this.sourceStore = store;
		this.sourceRequest = request as IPostRequest;

		// tell source store it is being used for delayed search
		store.isDelayedSearch = true;

		// reset store request since it should never be executed in full without some search text;
		// we want to prevent store.get from querying all (not required when using aggregate store as server will protect against querying all)
		if (!store.aggregateStoreParams) {
			store.request = null;
		}

		// save original query from store request so it can be modified by query string
		const postRequest = request as PostRequest;
		const query = postRequest?.binaryQuery;
		if (query) {
			this.sourceQuery = new Query();
			this.sourceQuery.importQuery(query);
		}
	}

	/** React */

	/**
	 * convert results to the format used in React components
	 * @returns {ComponentItem[]}
	 */
	getResultsReact(): ComponentItem[] {
		return convertNamesStoreDataToComponentItems(this.originalResults, this.sourceStore);
	}	
}
